// OrganizationStep.js — Step 1: Organisation details + address.
//
// Field layout (coarse → fine):
//   Row 1. Organisation Name                   (full width)
//   Row 2. Company Size | Annual Revenue        (2 cols)
//   Row 3. Region   | Country                  (Region hidden when the installation has one)
//   Row 4. Street Address                      (full width, Google Places autocomplete)
//   Row 5. ZIP | State / Province | City        (3 cols — ZIP auto-fills State + City)

import React, { useRef, useEffect, useMemo } from 'react';
import Input from '../../Input';
import Select from '../../Select';
import { getAllCountryOptions, getStateOptions } from '../../../utils/countries';
import { shouldShowRegionPicker, regionCountryCodes } from '../../../utils/regions';
import { useGoogleMapsLoader } from '../../../hooks/useGoogleMapsLoader';
import { useAddressAutocomplete } from '../../../hooks/useAddressAutocomplete';

const COMPANY_SIZE_OPTIONS = [
  { value: '',           label: '-- Select Company Size --' },
  { value: 'small',      label: 'Small (1–50 employees)' },
  { value: 'medium',     label: 'Medium (51–500 employees)' },
  { value: 'large',      label: 'Large (501–5,000 employees)' },
  { value: 'enterprise', label: 'Enterprise (5,000+ employees)' },
];

const ANNUAL_REVENUE_OPTIONS = [
  { value: '',        label: '-- Select Annual Revenue --' },
  { value: '<1M',     label: 'Less than $1M' },
  { value: '1-10M',   label: '$1M – $10M' },
  { value: '10-50M',  label: '$10M – $50M' },
  { value: '50-100M', label: '$50M – $100M' },
  { value: '>100M',   label: 'More than $100M' },
];

const OrganizationStep = ({
  formData,
  onChange,
  onBatchChange,
  errors,
  regions,
  onRegisterAsyncValidate,
}) => {
  const { isLoaded: googleIsLoaded, loadError: googleLoadError, isInactive: googleMapsInactive } =
    useGoogleMapsLoader();

  // ── Resolve the selected region + its address-autocomplete country codes ──
  const selectedRegion = regions.find((r) => String(r.regionId) === String(formData.regionId));
  const showRegionPicker = shouldShowRegionPicker(regions);

  // regionCodes MUST be memoized — a new array reference each render would
  // cause an infinite setState loop in useAddressAutocomplete's useEffect dep array.
  // Keyed on countryFilterKey (not the display name, which an operator may override).
  const regionCodes = useMemo(
    () => regionCountryCodes(selectedRegion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRegion?.countryFilterKey]
  );

  // Country dropdown options (all countries, not filtered by region)
  const countryOptions = getAllCountryOptions();

  const {
    suggestions,
    showSuggestions,
    setShowSuggestions,
    handleSuggestionSelect,
    zipLoading,
    validateAddress,
  } = useAddressAutocomplete({
    isLoaded:     googleIsLoaded,
    addressValue: formData.organizationAddress1,
    country:      formData.organizationCountry,
    regionCodes,
    zip:          formData.organizationZip,
    city:         formData.organizationCity,
    state:        formData.organizationState,
    onBatchChange,
  });

  // Register the async validator so the parent wizard can call it on Next
  useEffect(() => {
    if (onRegisterAsyncValidate) onRegisterAsyncValidate(validateAddress);
  }, [validateAddress, onRegisterAsyncValidate]);

  // Region select options
  const regionOptions = useMemo(
    () => [
      { value: '', label: '-- Select Region --' },
      ...regions.map((r) => ({ value: r.regionId, label: r.regionName })),
    ],
    [regions]
  );

  // Click-outside closes the address suggestion dropdown
  const addressWrapperRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [setShowSuggestions]);

  return (
    <div className="space-y-4">

      {/* Row 1 ── Organisation Name */}
      <Input
        label="Organization Name"
        name="organizationName"
        value={formData.organizationName}
        onChange={onChange}
        required
        error={errors.organizationName}
        placeholder="Enter your organization name"
      />

      {/* Row 2 ── Company Size | Annual Revenue */}
      <div className="grid grid-cols-2 gap-3">
        <Select
          label="Company Size"
          name="companySize"
          value={formData.companySize}
          onChange={onChange}
          options={COMPANY_SIZE_OPTIONS}
        />
        <Select
          label="Annual Revenue"
          name="annualRevenue"
          value={formData.annualRevenue}
          onChange={onChange}
          options={ANNUAL_REVENUE_OPTIONS}
        />
      </div>

      {/* Data Residency Region | Country — the region picker is hidden when the
          installation has one region (the wizard fills it in automatically) */}
      <div className={showRegionPicker ? 'grid grid-cols-2 gap-3' : ''}>
        {showRegionPicker && (
          <Select
            label="Data Residency Region"
            name="regionId"
            value={formData.regionId}
            onChange={onChange}
            options={regionOptions}
            error={errors.regionId}
            required
          />
        )}

        <Select
          label="Country"
          name="organizationCountry"
          value={formData.organizationCountry}
          onChange={onChange}
          options={countryOptions}
          error={errors.organizationCountry}
          required
        />
      </div>
      {/* No region to pick (none loaded): surface the required-field error */}
      {!showRegionPicker && errors.regionId && (
        <p className="-mt-2 text-sm text-risk-critical">{errors.regionId}</p>
      )}

      {/* Street Address with Google Places autocomplete */}
      <div ref={addressWrapperRef} className="relative">
        <Input
          label="Street Address"
          name="organizationAddress1"
          value={formData.organizationAddress1}
          onChange={onChange}
          required
          error={errors.organizationAddress1}
          placeholder={
            googleLoadError || googleMapsInactive ? 'Enter street address' : 'Start typing to search address…'
          }
          autoComplete="off"
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
        />

        {showSuggestions && suggestions.length > 0 && (
          <ul
            className="absolute z-50 w-full bg-white border border-[#d2d2d7] rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.10)] overflow-hidden"
            style={{ top: 'calc(100% - 1rem)', marginTop: '2px' }}
          >
            {suggestions.map((s, i) => (
              <li
                key={s.placePrediction?.placeId || i}
                className="border-b border-[#f0f0f0] last:border-b-0"
              >
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionSelect(s)}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#f5f5f7] transition-colors"
                >
                  <p className="text-sm font-medium text-[#1d1d1f] truncate">
                    {s.placePrediction?.mainText?.text}
                  </p>
                  {s.placePrediction?.secondaryText?.text && (
                    <p className="text-xs text-[#6e6e73] truncate mt-0.5">
                      {s.placePrediction.secondaryText.text}
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {googleLoadError && (
          <p className="text-xs text-[#86868b] mt-1">
            Address autocomplete unavailable — please enter manually.
          </p>
        )}
      </div>

      {/* Row 4 ── ZIP | State / Province | City */}
      <div className="grid grid-cols-3 gap-3">

        {/* ZIP */}
        <div>
          <Input
            label="ZIP / Postal"
            name="organizationZip"
            value={formData.organizationZip}
            onChange={onChange}
            required
            error={errors.organizationZip}
            placeholder="ZIP / Postal"
          />
          {zipLoading && (
            <p className="text-xs text-[#86868b] -mt-3 animate-pulse">
              Looking up…
            </p>
          )}
        </div>

        {/* State / Province */}
        {formData.organizationCountry === 'US' ? (
          <Select
            label="State"
            name="organizationState"
            value={formData.organizationState}
            onChange={onChange}
            options={getStateOptions()}
            error={errors.organizationState}
            required
          />
        ) : (
          <Input
            label="State / Province"
            name="organizationState"
            value={formData.organizationState}
            onChange={onChange}
            required
            error={errors.organizationState}
            placeholder="State / Province"
          />
        )}

        {/* City */}
        <Input
          label="City"
          name="organizationCity"
          value={formData.organizationCity}
          onChange={onChange}
          required
          error={errors.organizationCity}
          placeholder="City"
        />
      </div>

    </div>
  );
};

export default OrganizationStep;
